import { useState, useEffect } from "react";
import { AuctionItem } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import AnimBtn from "@/components/base/AnimBtn";

interface Props {
  auction: AuctionItem;
  onBid: (auction: AuctionItem) => void;
}

function useCountdown(endTime: string) {
  const calc = () => {
    const diff = new Date(endTime).getTime() - Date.now();
    if (diff <= 0) return { h: 0, m: 0, s: 0, expired: true };
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { h, m, s, expired: false };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [endTime]);
  return time;
}

export default function AuctionCard({ auction, onBid }: Props) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const countdown = useCountdown(auction.endTime);
  const [hovered, setHovered] = useState(false);

  const isMyAuction = user?.id === auction.sellerId;
  const isTopBidder = user?.username === auction.currentBidder;

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";

  const urgency = countdown.h < 1 && !countdown.expired;

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-all duration-300 ${hovered ? "scale-[1.02] -translate-y-1" : "scale-100"} ${cardBg}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top bar */}
      <div className={`px-5 py-3 flex items-center justify-between border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
        <div className="flex items-center gap-2">
          {isMyAuction && (
            <span className="text-xs bg-emerald-400/15 text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5 font-semibold whitespace-nowrap animate-slide-up-fade">
              自分の出品
            </span>
          )}
          {isTopBidder && (
            <span className="text-xs bg-amber-400/15 text-amber-400 border border-amber-400/30 rounded-full px-2 py-0.5 font-semibold whitespace-nowrap animate-glow-pulse">
              最高入札中
            </span>
          )}
          {!isMyAuction && !isTopBidder && (
            <span className={`text-xs rounded-full px-2 py-0.5 font-semibold whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
              進行中
            </span>
          )}
        </div>
        {/* Countdown */}
        <div className={`flex items-center gap-1.5 text-xs font-mono font-bold ${urgency ? "text-red-400" : countdown.expired ? "text-gray-500" : "text-amber-400"}`}>
          <i className={`${urgency ? "ri-alarm-warning-line animate-pulse" : "ri-time-line"} text-sm`}></i>
          {countdown.expired
            ? "終了"
            : `${String(countdown.h).padStart(2, "0")}:${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`}
        </div>
      </div>

      <div className="p-5">
        {/* Coords */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className={`text-xs mb-0.5 ${textSecondary}`}>区画座標</p>
            <p className={`font-mono font-bold text-sm ${textPrimary}`}>
              {auction.blockLat.toFixed(4)}, {auction.blockLng.toFixed(4)}
            </p>
          </div>
          <div className="text-right">
            <p className={`text-xs mb-0.5 ${textSecondary}`}>期間</p>
            <span className={`text-xs rounded-full px-2.5 py-1 font-semibold whitespace-nowrap ${isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              {auction.duration}
            </span>
          </div>
        </div>

        {/* Bid info */}
        <div className={`rounded-xl p-4 mb-4 transition-all ${isDark ? "bg-amber-400/5 border border-amber-400/15" : "bg-amber-50 border border-amber-200"} ${hovered ? "border-amber-400/40" : ""}`}>
          <div className="flex items-end justify-between">
            <div>
              <p className={`text-xs mb-1 ${textSecondary}`}>現在の最高入札</p>
              <p className={`text-2xl font-black text-amber-400 transition-all ${hovered ? "scale-105" : ""}`}>
                {auction.currentBid.toLocaleString()}
              </p>
              <p className={`text-xs ${textSecondary}`}>coins</p>
            </div>
            <div className="text-right">
              <p className={`text-xs mb-1 ${textSecondary}`}>入札者</p>
              <p className={`text-sm font-semibold ${auction.currentBidder ? textPrimary : textSecondary}`}>
                {auction.currentBidder ?? "なし"}
              </p>
            </div>
          </div>
        </div>

        {/* Seller */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${isDark ? "bg-white/10 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              {auction.sellerName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className={`text-xs ${textSecondary}`}>出品者</p>
              <p className={`text-xs font-semibold ${textPrimary}`}>{auction.sellerName}</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-xs ${textSecondary}`}>開始価格</p>
            <p className={`text-xs font-semibold ${textPrimary}`}>{auction.startPrice} coins</p>
          </div>
        </div>

        {/* Action */}
        {!countdown.expired && !isMyAuction && (
          <AnimBtn
            onClick={() => onBid(auction)}
            animType="bounce"
            coinEffect
            className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-auction-line text-base"></i>
            入札する
          </AnimBtn>
        )}
        {isMyAuction && !countdown.expired && (
          <div className={`w-full rounded-xl py-3 text-sm text-center font-medium ${isDark ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-emerald-50 text-emerald-600 border border-emerald-200"}`}>
            <i className="ri-shield-check-line mr-1.5"></i>
            あなたの出品中
          </div>
        )}
        {countdown.expired && (
          <div className={`w-full rounded-xl py-3 text-sm text-center font-medium ${isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400"}`}>
            オークション終了
          </div>
        )}
      </div>
    </div>
  );
}
